import React, { useState, useEffect } from "react";
import TableComponent from "../../TableComponent/TableComponent";
import IconButton from "../../IconButton/IconButton";
import ModalComponent from "../../ModalComponent/ModalComponent";
import PlusIcon from "../../../../assets/PlusIcon";
import Dropdown from "../../Dropdown/Dropdown";
import "./RCMManagement.css";
import { getAllRCMUsers, createNewRCMUser, updateRCMUser, getAllRoles, getAllRegionsWithCountry, findRCMUsers, revokeRCMUser } from "../../../../services/svt/svt-admin/rcm-management.service";
import Loader from "../../../common/Loader/Loader";
import validateFields from '../../../../utils/ValidationInput';
import ViewRCMUserDetails from "./ViewRCMUserDetails";
import ConfirmationModal from "../../../common/ModalComponent/ModalComponent";
import { toast } from "react-toastify";
import { DataTableComponent } from "../../../common/TableComponent/DataTableComponent";
import { formatDate } from "../../../../utils/dateFormat";
import { useTranslation, Trans } from "react-i18next";

const columns = [
    { name: "Email", field: "email", selector: row => row.email, sortable: true, width: "200px" },
    { name: "First Name", field: "firstName", selector: row => row.firstName, sortable: true },
    { name: "Last Name", field: "lastName", selector: row => row.lastName, sortable: true },
    // { headerName: "Role", field: "role" },
    { name: "Created Date", field: "createdDate", selector: row => row.createdDate, sortable: true },
    { name: "Created By", field: "createdBy", selector: row => row.createdBy, sortable: true },
    { name: "Comments", field: "remarks", selector: row => row.remarks, sortable: true },
];

const getColumns = (t) => [
    { name: t("email"), field: "email", selector: row => row.email, sortable: true, width: "200px" },
    { name: t("firstName"), field: "firstName", selector: row => row.firstName, sortable: true },
    { name: t("lastName"), field: "lastName", selector: row => row.lastName, sortable: true },
    { name: t("createdDate"), field: "createdDate", selector: row => row.createdDate, sortable: true },
    { name: t("createdBy"), field: "createdBy", selector: row => row.createdBy, sortable: true },
    { name: t("comments"), field: "remarks", selector: row => row.remarks, sortable: true },
];

const getNewRCMData = (t) => [
    { fieldName: t('firstName'), name: "firstName", fieldValue: '', type: 'input', required: true },
    { fieldName: t('lastName'), name: "lastName", fieldValue: '', type: 'input', required: true },
    { fieldName: t('email'), name: "email", fieldValue: '', type: 'input', required: true },
    { fieldName: t('roles'), name: "roles", fieldValue: '', type: 'dropdown', options: [], isFieldDisabled: false, required: true },
    {
        fieldName: t('region'), name: "regionName", fieldValue: '', type: 'dropdown', isFieldDisabled: true,
        options: [],
        required: true
    },
    {
        fieldName: t('country'), name: "countryName", fieldValue: '', type: 'multiselect', isFieldDisabled: true,
        options: [],
        required: true
    },
    { fieldName: t('comments'), name: "remarks", fieldValue: '', type: 'textarea' }
];

// Helper to get translated fields with roles
const getTranslatedRCMDataWithRoles = async (t) => {
    const rolesList = await getAllRoles();
    const filteredList = (rolesList?.data?.filter(item => (item.name === 'RCM-COUNTRY' || item.name === 'RCM-REGION'))).reverse();
    return getNewRCMData(t).map(item => ({
        ...item,
        options: item.name === 'roles'
            ? filteredList?.map((roleItem) => ({
                ...roleItem,
                value: roleItem.id,
                label: roleItem.name
            }))
            : item?.options
    }));
};

const RCMManagement = () => {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [RCMData, setRCMData] = useState(getNewRCMData(t));
    const [tableData, setTableData] = useState([]);
    const [tableColumns, setTableColumns] = useState(columns);
    const [modalType, setModalType] = useState('');
    const [selectedRowIndex, setSelectedRowIndex] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedRegion, setSelectedRegion] = useState('');
    const [countryList, setCountryList] = useState([]);
    const [regionList, setRegionList] = useState([]);
    const [modalRegions, setModalRegions] = useState([{ regionId: '', countryIds: [] }]);
    const [modalCountries, setModalCountries] = useState([]);
    const [selectedCountryList, setSelectedCountryList] = useState([]);
    const [roleType, setRoleType] = useState('');
    const [allRegions, setAllRegions] = useState([]);
    const [payloadRegionList, setPayloadRegionList] = useState([]);
    const [showRCMDetailsPage, setShowRCMDetailsPage] = useState(false);
    const [RCMUserDetailsData, setRCMUserDetailsData] = useState('');
    const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');
    const [countryData, setCountryData] = useState([]);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        organizationLevel: '',
        country: '',
        region: '',
        remarks: '',
        roles: ''
    });

    useEffect(() => {
        setTableColumns(getColumns(t));
        // Use the helper to set translated fields with roles
        getTranslatedRCMDataWithRoles(t).then(setRCMData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    useEffect(() => {
        fetchData();  //Used for fetching RCM users
        fetchRoles();  //Used for fetching roles
        fetchAllRegionsWithCountry();  //Used for fetching all regions
    }, []);

    // Fetch all slots
    const fetchData = async () => {
        try {
            setLoading(true);
            const rcmResult = await getAllRCMUsers();
            const updatedData = rcmResult?.data;
            setTableData(updatedData);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch all roles
    const fetchRoles = async () => {
        try {
            setLoading(true);
            const rolesList = await getAllRoles();
            const filteredList = (rolesList?.data?.filter(item => (item.name === 'RCM-COUNTRY' || item.name === 'RCM-REGION'))).reverse();
            const updatedData = RCMData?.map(item => ({
                ...item,
                options: item.name === 'roles' ? (
                    filteredList?.map((roleItem) => ({
                        ...roleItem,
                        value: roleItem.id,
                        label: roleItem.name
                    }))
                ) : item?.options
            }));
            setRCMData(updatedData);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch all regions with country
    const fetchAllRegionsWithCountry = async () => {
        try {
            setLoading(true);
            const allRegionsList = await getAllRegionsWithCountry();
            if (allRegionsList !== null) {
                const updatedData = allRegionsList?.data?.map((regionItem) => ({
                    ...regionItem,
                    value: regionItem.regionId,
                    label: regionItem.regionName
                }))
                setRegionList(updatedData);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            organizationLevel: '',
            // countryName: '',
            // regionName: '',
            remarks: '',
            roles: ''
        })
        setModalCountries([]);
        setModalRegions([]);
        setSelectedCountryList([]);
        setPayloadRegionList([]);
        setCountryData([]);
        setSelectedCountry('');

        const updatedData = RCMData?.map(item => ({
            ...item,
            isFieldDisabled: (item.name === 'regionName' || item.name === 'countryName') ? true : false,
            options: (item.name === 'regionName' || item.name === 'countryName') ? [] : item?.options
        }));
        setRCMData(updatedData);
    }

    const onAddNewRCM = () => {
        setIsModalOpen(true);
        setModalType('add');
        setSelectedRegion('');
        setSelectedCountry('');
        fetchData();
    };

    const onChangeInput = async (inputData, label) => {
        const { name, value } = inputData;
        toast.dismiss();
        if (name === "firstName" && value.length > 30) {
            toast.error(t("firstNameCannotExceed", { number: 30 }), { position: "top-center" });
            return;
        }
        if (name === "lastName" && value.length > 30) {
            toast.error(t("lastNameCannotExceed", { number: 30 }), { position: "top-center" });
            return;
        }
        if (name === "remarks" && value.length > 255) {
            toast.error(t("commentsCannotExceed", { number: 255 }), { position: "top-center" });
            return;
        }
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));

        if (name === 'roles') {
            setFormData((prevData) => ({
                ...prevData,
                regionName: '',
                countryName: []
            }));
            setRoleType(label);
            setModalCountries([]);
            setModalRegions([]);
            const allRegionsList = await getAllRegionsWithCountry();
            setAllRegions(allRegionsList?.data);
            if (label === 'RCM-COUNTRY') {
                const updatedData = RCMData?.map(item => ({
                    ...item,
                    isFieldDisabled: item.name === 'regionName' ? false : item?.isFieldDisabled,
                    options: item.name === 'regionName' ? (
                        allRegionsList?.data?.map((regionItem) => ({
                            ...regionItem,
                            value: regionItem.regionId,
                            label: regionItem.regionName
                        }))
                    ) : item?.options
                }));
                setRCMData(updatedData);
            } else if (label === 'RCM-REGION') {
                const updatedData = RCMData?.map(item => ({
                    ...item,
                    isFieldDisabled: item.name === 'regionName' ? false : item.name === 'countryName' ? true : item?.isFieldDisabled,
                    options: item.name === 'regionName' ? (
                        allRegionsList?.data?.map((regionItem) => ({
                            ...regionItem,
                            value: regionItem.regionId,
                            label: regionItem.regionName
                        }))
                    ) : item?.options
                }));
                setRCMData(updatedData);
            }
        }

        if (name === 'regionName') {
            setSelectedCountryList([]);
            setModalCountries([]);
            setFormData((prevData) => ({
                ...prevData,
                countryName: []
            }));

            let regions = [
                { regionId: value, countryIds: [] }
            ]
            setModalRegions(regions);
            setPayloadRegionList(regions);

            if (roleType === 'RCM-COUNTRY') {
                const selectedRegionCountries = regionList.filter((item) => item.regionId === parseInt(value))
                let allCountries = selectedRegionCountries[0].countries;

                const updatedData = RCMData?.map(item => ({
                    ...item,
                    isFieldDisabled: item.name === 'countryName' ? false : item?.isFieldDisabled,
                    options: item.name === 'countryName' ? (
                        allCountries?.map((item) => ({
                            ...item,
                            value: item.countryId,
                            label: item.countryName
                        }))
                    ) : item?.options
                }));
                setRCMData(updatedData);
            }
        }
    };

    const onChangeMultiselect = (data, type) => {
        if (type === 'regionName') {
            let regions = data.map((item) => ({ 'regionId': item.regionId, 'countryIds': [] }));
            setModalRegions(regions);
            setPayloadRegionList(regions);

            if (roleType === 'RCM-COUNTRY' && regions.length !== 0) {
                let countries = data.map((item) => item.countries);
                let allCountries = countries.flat();
                setModalCountries(allCountries);

                const updatedData = RCMData?.map(item => ({
                    ...item,
                    isFieldDisabled: item.name === 'countryName' ? false : item?.isFieldDisabled,
                    options: item.name === 'countryName' ? (
                        allCountries?.map((item) => ({
                            ...item,
                            value: item.countryId,
                            label: item.countryName
                        }))
                    ) : item?.options
                }));
                setRCMData(updatedData);
            }
        } else {
            setCountryData(data);
            const updatedData = modalRegions?.map(item => ({
                ...item,
                countryIds: data.reduce((acc, country) => {
                    if (parseInt(item.regionId) === getRegionByCountry(country.countryName)) {
                        acc.push(country.countryId);
                    }
                    return acc;
                }, [])
            }));

            setFormData((prevData) => ({
                ...prevData,
                countryName: data,
            }));
            setPayloadRegionList(updatedData);
        }
    };

    const getRegionByCountry = (countryName) => {
        for (let region of allRegions) {
            const country = region.countries.find(item => item.countryName === countryName);
            if (country) {
                return region.regionId;
            }
        }
    }

    const trimText = (text) => {
        return text.trim().replace(/\s+/g, ' ');
    };

    const saveData = async () => {
        setLoading(true);
        // Required fields with types (text or email)
        let regionField = '';
        if (payloadRegionList.length === 0) regionField = '';
        else regionField = payloadRegionList.length;

        let countryField = '';
        if (countryData.length === 0) countryField = '';
        else countryField = countryData.length;

        const requiredFields = [
            { label: t("firstName"), key: "First Name", type: "text", value: trimText(formData.firstName) },
            { label: t("lastName"), key: "Last Name", type: "text", value: trimText(formData.lastName) },
            { label: t("email"), key: "Email", type: "email", value: trimText(formData.email) },
            { label: t("roles"), key: "Roles", type: "dropdown", value: formData.roles },
            { label: t("region"), key: "Region", type: "dropdown", value: regionField },
        ];
        if (roleType === 'RCM-COUNTRY') {
            requiredFields.push({ label: t("country"), key: "Country", type: "dropdown", value: countryField })
        }
        const isValid = validateFields(formData, requiredFields, 'byValue');
        if (!isValid) {
            setLoading(false);
            return;
        }

        let payload = {
            firstName: trimText(formData.firstName),
            lastName: trimText(formData.lastName),
            emailId: trimText(formData.email),
            roleId: parseInt(formData.roles),
            regionsWithCountries: payloadRegionList,
            // countryId: selectedCountryList,
            // regionId: modalRegions,
            remarks: formData.remarks,
        }
        await createNewRCMUser(payload).then((response) => {
            if (response === null) {
                setLoading(false);
            } else {
                fetchData();
                closeModal();
                setLoading(false);
            }
        })
    };

    const updateData = async () => {
        setLoading(true);
        let payload = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            emailId: formData.email,
            roleId: formData.roles,
            regionsWithCountries: payloadRegionList,
            // countryId: selectedCountryList,
            // regionId: modalRegions,
            remarks: formData.remarks,
        }
        await updateRCMUser(payload).then((response) => {
            if (response === null) {
                setLoading(false);
            } else {
                fetchData();
                closeModal();
                setLoading(false);
            }
        })
    };

    const onEditDetails = (data, selectedIndex) => {
        setModalType('edit');
        setIsModalOpen(true);
        setSelectedRowIndex(selectedIndex);

        let { firstName, lastName, email, organizationLevel, countryName, regionName, remarks } = data;
        setFormData({
            firstName: firstName,
            lastName: lastName,
            email: email,
            organizationLevel: organizationLevel,
            countryName: countryName,
            regionName: regionName,
            remarks: remarks,
        })
    };

    const onChangeDropdownVal = (e) => {
        const { name, value } = e.target;
        if (name === 'region') {
            if (value !== '') {
                setSelectedRegion(value);
                let selectedRegion = (regionList?.filter((item) => item.regionId === parseInt(value)));
                let countriesList = selectedRegion[0].countries;
                countriesList = countriesList.map((obj) => ({
                    ...obj,
                    label: obj.countryName,
                    value: obj.countryId
                }))
                setCountryList(countriesList)
                setSelectedCountry('');
            } else {
                setSelectedCountry('');
                setCountryList([]);
                setSelectedRegion('');
            }
        }
        else if (name === 'country') setSelectedCountry(value);
    };

    const onFindRCM = async () => {
        setLoading(true);
        const payload = {
            countryId: selectedCountry,
            regionId: selectedRegion
        }
        await findRCMUsers(payload).then((response) => {
            const updatedData = response?.data;
            setTableData(updatedData);
            setLoading(false);
            // setSelectedCountry('');
            // setSelectedRegion('');
        })
    };

    const onRevokeUser = () => {
        let payload = {
            userId: RCMUserDetailsData?.id,
            roleId: selectedUser?.roleId,
            regionId: selectedUser?.regionId,
            // countryId: row?.countryId
        }
        if (selectedUser?.countryIds.length !== 0) {
            payload.countryId = selectedUser?.countryIds[0]
        }
        revokeRCMUser(payload).then((response) => {
            if (response !== null) {
                toast.success(t("successfullyRevokedRCMRole", { email: RCMUserDetailsData?.email }), { position: "top-center" });
                fetchUserLocations(RCMUserDetailsData?.id);
                setIsRevokeModalOpen(false);
                setSelectedUser('');
            }
        })
    };
    const fetchUserLocations = async (userId) => {
        try {
            setLoading(true);
            const rcmResult = await getAllRCMUsers();
            const updatedData = rcmResult?.data;
            const filteredUserData = updatedData.filter((item) => item.id === userId);
            if (filteredUserData.length !== 0) {
                setRCMUserDetailsData(filteredUserData[0]);
            } else {
                setShowRCMDetailsPage(false);
            }
            setTableData(updatedData);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };
    const onViewRCMDetails = (data, selectedIndex) => {
        setRCMUserDetailsData(data);
        setShowRCMDetailsPage(true);
    };
    const onCloseViewRCMDetails = () => {
        setShowRCMDetailsPage(false)
    }

    // Handle Open Revoke Modal
    const handleOpenRevokeModal = (row) => {
        setSelectedUser(row);
        setIsRevokeModalOpen(true);
    };

    // Cancel Revoke
    const handleCancelRevoke = () => {
        setIsRevokeModalOpen(false);
        setSelectedUser('');
    };

    return (
        <>
            {loading ? <Loader /> :
                <div className={["glbOuterWrapper", "rcmMgmt"].join(' ')}>
                    {!showRCMDetailsPage ?
                        <>
                            <div className="searchBarOuterDiv">
                                <div className="searchBar">
                                    <div>
                                        <div className="dropdownLabel">{t("region")}</div>
                                        <Dropdown value={selectedRegion} handelChangeInput={onChangeDropdownVal} options={regionList} name="region" defaultLabel={t("selectRegion")} />
                                    </div>

                                    <div>
                                        <div className="dropdownLabel">{t("country")}</div>
                                        <Dropdown value={selectedCountry} handelChangeInput={onChangeDropdownVal} options={countryList} name="country" defaultLabel={t("selectCountry")} />
                                    </div>

                                    <div style={{ marginTop: '1.5rem' }}>
                                        <IconButton
                                            text={t("findRCM")}
                                            onClick={onFindRCM}
                                            style={{ backgroundColor: "#F0F0F0", color: '#454647', border: '1px solid #B3B2B4', height: '1.8rem' }}
                                            icon=""
                                        />
                                    </div>

                                </div>
                                <div className="buttonOuterDiv">
                                    <IconButton
                                        text={t("addRCM")}
                                        onClick={onAddNewRCM}
                                        style={{ backgroundColor: "#F0F0F0", color: '#454647', border: '1px solid #B3B2B4' }}
                                        icon={<PlusIcon />}
                                    />
                                </div>
                            </div>

                            <div style={{ marginTop: '1rem' }}>
                                <DataTableComponent
                                    columns={tableColumns}
                                    data={tableData}
                                    onEditDetails={onEditDetails}
                                    // onRevokeUser={handleOpenRevokeModal}
                                    tableName="rcm"
                                    noDataMessage={t("noDataFound")}
                                    onViewRCMDetails={onViewRCMDetails}
                                    pageCount={5}
                                />
                                {/* <TabComponent onSelectTab={onSelectTab} tabs={tabsList} tabContent={tabContent} /> */}
                            </div>

                            <ModalComponent
                                modalTitle={modalType === 'add' ? t("addNewRCM") : t("editRCM")}
                                isOpen={isModalOpen}
                                fieldsData={RCMData}
                                formData={formData}
                                modalType={modalType}
                                onClose={closeModal}
                                onChangeInput={onChangeInput}
                                onChangeMultiselect={onChangeMultiselect}
                                buttons={[
                                    <button className="saveButton" onClick={modalType === 'add' ? saveData : updateData}>{modalType === 'add' ? t("add") : t("update")}</button>,
                                    <button className="cancelButton" onClick={closeModal}>{t("cancel")}</button>
                                ]}
                            />
                        </>
                        :
                        <ViewRCMUserDetails onCloseViewRCMDetails={onCloseViewRCMDetails} data={RCMUserDetailsData} onRevokeUser={handleOpenRevokeModal} />
                    }

                    {/* Revoke Confirmation Modal */}
                    <ConfirmationModal
                        isOpen={isRevokeModalOpen}
                        onClose={handleCancelRevoke}
                        modalTitle={t("revokeUser")}
                        modalText={
                            <p>
                                <Trans i18nKey="areYouSureRevokeRCM" />
                            </p>
                        }
                        buttons={[
                            <button key="revoke" className="survey-upload-btn" onClick={onRevokeUser}>
                                {t("revoke")}
                            </button>,
                            <button key="cancel" className="survey-cancel-btn" onClick={handleCancelRevoke}>
                                {t("cancel")}
                            </button>,
                        ]}
                    />
                </div>
            }
        </>
    );
}

export default RCMManagement;
